// client/src/pages/wholesaler/ProductForm.js
// ================================================================
// PRODUCT FORM — Updated with Category Registry & i18n
// Categories and units fetched from API, grouped unit dropdown,
// bilingual labels, subcategory field for Phase B
// ================================================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { wholesalerAPI, registryAPI } from '../../services/api';
import useCategoryLabels from '../../hooks/useCategoryLabels';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import { getImageUrl } from '../../utils/imageHelper';
import toast from 'react-hot-toast';

const ProductForm = () => {
  const { t, i18n } = useTranslation(['wholesaler', 'products', 'common']);
  const nameInputRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    brand: '',
    sku: '',
    pricing: {
      unitPrice: '',
      unit: 'piece',
      minOrderQuantity: 1,
      bulkPricing: []
    },
    specifications: { origin: '', weight: '', shelfLife: '' },
    availability: { inStock: true, stockQuantity: '', leadTime: 1 },
    certifications: [],
    images: []
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);

  // Registry-based categories & units (fetched from API)
  const [categories, setCategories] = useState([]);
  const [unitGroups, setUnitGroups] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(true);

  useEffect(() => { fetchRegistry(); fetchSubcategories(); }, [i18n.language]);

  const fetchRegistry = async () => {
    try {
      setRegistryLoading(true);
      const lang = i18n.language === 'el' ? 'el' : 'en';
      const [catRes, unitRes] = await Promise.all([
        registryAPI.getCategories(lang),
        registryAPI.getUnits(lang, true)
      ]);
      if (catRes.data?.data) setCategories(catRes.data.data);
      if (unitRes.data?.data) setUnitGroups(unitRes.data.data);
    } catch (err) {
      console.error('Failed to load registry:', err);
      // Fallback minimal set
      setCategories([
        { key: 'fruits_vegetables', label: 'Fruits & Vegetables', icon: '🥬' },
        { key: 'dairy_eggs', label: 'Dairy & Eggs', icon: '🧀' },
        { key: 'meat_poultry', label: 'Meat & Poultry', icon: '🥩' },
        { key: 'seafood', label: 'Seafood', icon: '🐟' },
        { key: 'bakery_pastries', label: 'Bakery & Pastries', icon: '🥐' },
        { key: 'beverages', label: 'Beverages', icon: '🥤' },
        { key: 'dry_goods_pantry', label: 'Dry Goods & Pantry', icon: '🫘' },
        { key: 'frozen_foods', label: 'Frozen Foods', icon: '🧊' },
        { key: 'condiments_sauces', label: 'Condiments & Sauces', icon: '🫙' },
        { key: 'cleaning_supplies', label: 'Cleaning Supplies', icon: '🧹' },
        { key: 'disposables_packaging', label: 'Disposables & Packaging', icon: '📦' },
        { key: 'kitchen_equipment', label: 'Kitchen Equipment', icon: '🍳' },
        { key: 'other', label: 'Other', icon: '📋' },
      ]);
      setUnitGroups([
        { key: 'weight', label: 'Weight', units: [{ key: 'kg', label: 'kg' }, { key: 'g', label: 'g' }] },
        { key: 'volume', label: 'Volume', units: [{ key: 'liter', label: 'Liter' }, { key: 'ml', label: 'mL' }] },
        { key: 'count', label: 'Count', units: [{ key: 'piece', label: 'Piece' }, { key: 'dozen', label: 'Dozen' }] },
        { key: 'package', label: 'Package', units: [{ key: 'box', label: 'Box' }, { key: 'case', label: 'Case' }, { key: 'pack', label: 'Pack' }, { key: 'bottle', label: 'Bottle' }] },
      ]);
    } finally { setRegistryLoading(false); }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await wholesalerAPI.getSubcategories();
      setSubcategories(res.data?.data || []);
    } catch (err) { /* no subcategories yet */ }
  };

  // Subcategories filtered by selected category
  const filteredSubcategories = formData.category
    ? subcategories.filter(s => s.parentCategory === formData.category)
    : [];

  const certificationOptions = ['ISO 9001','ISO 22000','HACCP','BRC','IFS','Organic','Bio','Halal','Kosher','Non-GMO'];

  useEffect(() => { if (isEditMode) fetchProduct(); }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await wholesalerAPI.getProductById(id);
      const product = response.data;
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        subcategory: product.subcategory || '',
        brand: product.brand || '',
        sku: product.sku || '',
        pricing: {
          unitPrice: product.pricing?.unitPrice || product.price || '',
          unit: product.pricing?.unit || product.unit || 'piece',
          minOrderQuantity: product.pricing?.minOrderQuantity || product.pricing?.minimumQuantity || product.minimumOrder || 1,
          bulkPricing: product.pricing?.bulkPricing || []
        },
        specifications: product.specifications || { origin: '', weight: '', shelfLife: '' },
        availability: {
          inStock: product.availability?.inStock !== undefined ? product.availability.inStock : true,
          stockQuantity: product.availability?.stockQuantity || product.stock || '',
          leadTime: product.availability?.leadTime || 1
        },
        certifications: product.certifications || [],
        images: []
      });
      if (product.images?.length > 0) setExistingImages(product.images);
    } catch (err) {
      setError(t('wholesaler:productForm.fetchError', 'Failed to fetch product details.'));
    } finally { setLoading(false); }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: type === 'checkbox' ? checked : value } }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        // Clear subcategory when category changes
        ...(name === 'category' ? { subcategory: '' } : {})
      }));
    }
  };

  const handleCertificationChange = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert) ? prev.certifications.filter(c => c !== cert) : [...prev.certifications, cert]
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + existingImages.length > 5) { toast.error(t('wholesaler:productForm.maxImages', 'Maximum 5 images allowed')); return; }
    setImageFiles(files);
    setImagePreviews(files.map(file => URL.createObjectURL(file)));
  };

  const removeExistingImage = (index) => setExistingImages(existingImages.filter((_, i) => i !== index));
  const removeNewImage = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e, saveType = 'save-and-close') => {
    e.preventDefault();
    setError(null);
    if (!formData.name || !formData.category || !formData.pricing.unitPrice || !formData.pricing.unit) {
      setError(t('wholesaler:productForm.requiredFields', 'Please fill in all required fields.')); return;
    }
    if (formData.pricing.unitPrice <= 0) { setError(t('wholesaler:productForm.pricePositive', 'Price must be > 0.')); return; }

    try {
      setLoading(true);
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description || '');
      data.append('category', formData.category);
      if (formData.subcategory) data.append('subcategory', formData.subcategory);
      data.append('brand', formData.brand || '');
      if (formData.sku?.trim()) { data.append('sku', formData.sku.trim()); }
      else if (!isEditMode) { data.append('sku', `TEMP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`); }

      data.append('pricing', JSON.stringify({
        unitPrice: parseFloat(formData.pricing.unitPrice) || 0,
        unit: formData.pricing.unit || 'piece',
        minOrderQuantity: parseInt(formData.pricing.minOrderQuantity) || 1,
        bulkPricing: formData.pricing.bulkPricing || []
      }));
      data.append('specifications', JSON.stringify(formData.specifications || {}));
      data.append('availability', JSON.stringify({
        inStock: formData.availability?.inStock !== false,
        stockQuantity: formData.availability?.stockQuantity ? parseInt(formData.availability.stockQuantity) : null,
        leadTime: parseInt(formData.availability?.leadTime) || 1
      }));
      if (formData.certifications?.length > 0) data.append('certifications', JSON.stringify(formData.certifications));
      if (isEditMode) data.append('existingImages', JSON.stringify(existingImages));
      imageFiles.forEach(file => data.append('images', file));

      if (isEditMode) {
        await wholesalerAPI.updateProduct(id, data);
        toast.success(t('wholesaler:productForm.updateSuccess', 'Product updated!'));
        navigate('/wholesaler/products');
      } else {
        const response = await wholesalerAPI.createProduct(data);
        if (response.data.warning) toast.error(response.data.warning.message);
        if (saveType === 'save-and-add-another') {
          const keepCat = formData.category, keepUnit = formData.pricing.unit;
          setFormData({ name:'',description:'',category:keepCat,subcategory:'',brand:'',sku:'',
            pricing:{unitPrice:'',unit:keepUnit,minOrderQuantity:1,bulkPricing:[]},
            specifications:{origin:'',weight:'',shelfLife:''},
            availability:{inStock:true,stockQuantity:'',leadTime:1},certifications:[],images:[]
          });
          setImageFiles([]); setImagePreviews([]); setExistingImages([]);
          setTimeout(() => { nameInputRef.current?.focus(); window.scrollTo({top:0,behavior:'smooth'}); }, 100);
          toast.success(t('wholesaler:productForm.addedAnother', 'Product added! Add another?'));
        } else {
          toast.success(t('wholesaler:productForm.createSuccess', 'Product created!'));
          navigate('/wholesaler/products');
        }
      }
    } catch (err) {
      if (err.response?.data?.error === 'PRODUCT_LIMIT_REACHED') {
        setConfirmModal({
          title: t('wholesaler:productForm.limitReachedTitle', 'Product Limit Reached'),
          message: err.response.data.message + '\n\nView upgrade plans?',
          icon: 'warning',
          confirmLabel: t('wholesaler:productForm.viewPlans', 'View Plans'),
          confirmColor: 'blue',
          onConfirm: () => { setConfirmModal(null); navigate('/wholesaler/pricing'); }
        });
      } else { setError(err.response?.data?.message || t('wholesaler:productForm.saveFailed', 'Failed to save.')); }
    } finally { setLoading(false); }
  };

  if (loading && isEditMode && !formData.name) {
    return (<div className="min-h-screen bg-gray-50"><Navbar /><div className="flex justify-center items-center h-64"><LoadingSpinner size="lg" /></div><Footer /></div>);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">
            {isEditMode ? t('wholesaler:productForm.editTitle', 'Edit Product') : t('wholesaler:productForm.addTitle', 'Add New Product')}
          </h1>
          {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.basicInfo', 'Basic Information')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.productName', 'Product Name')} *</label>
                  <input ref={nameInputRef} type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.category', 'Category')} *</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required>
                    <option value="">{t('wholesaler:productForm.selectCategory', 'Select Category')}</option>
                    {categories.map(cat => (<option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>))}
                  </select>
                </div>
                {/* Subcategory — always shown when a category is selected */}
                {formData.category && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.subcategory', 'Subcategory')}</label>
                  {filteredSubcategories.length > 0 ? (
                    <select name="subcategory" value={formData.subcategory} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                      <option value="">{t('wholesaler:productForm.noSubcategory', '— None —')}</option>
                      {filteredSubcategories.map(sub => (
                        <option key={sub.key} value={sub.key}>{i18n.language === 'el' ? sub.label_el : sub.label_en}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-500">
                      {t('wholesaler:productForm.noSubcategoriesHint', 'No subcategories defined for this category yet. You can create custom subcategories in Profile → Subcategories.')}
                    </div>
                  )}
                </div>
                )}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.brand', 'Brand')}</label>
                  <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.skuLabel', 'SKU (Optional)')}</label>
                  <input type="text" name="sku" value={formData.sku} onChange={handleInputChange} placeholder={t('wholesaler:productForm.skuPlaceholder', 'e.g., PRD-001')} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.description', 'Description')}</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
              </div>
            </div>

            {/* Pricing with grouped unit dropdown */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.pricingSection', 'Pricing')}</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.unitPrice', 'Unit Price (€)')} *</label>
                  <input type="number" name="pricing.unitPrice" value={formData.pricing.unitPrice} onChange={handleInputChange} step="0.01" min="0" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.unit', 'Unit')} *</label>
                  <select name="pricing.unit" value={formData.pricing.unit} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required>
                    {unitGroups.map(group => (
                      <optgroup key={group.key} label={group.label}>
                        {group.units.map(unit => (<option key={unit.key} value={unit.key}>{unit.label}</option>))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.minOrder', 'Min Order Qty')}</label>
                  <input type="number" name="pricing.minOrderQuantity" value={formData.pricing.minOrderQuantity} onChange={handleInputChange} min="1" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.specifications', 'Specifications')}</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.origin', 'Origin')}</label>
                  <input type="text" name="specifications.origin" value={formData.specifications.origin} onChange={handleInputChange} placeholder={t('wholesaler:productForm.originPlaceholder', 'e.g., Greece')} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.weight', 'Weight/Size')}</label>
                  <input type="text" name="specifications.weight" value={formData.specifications.weight} onChange={handleInputChange} placeholder="e.g., 1kg" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.shelfLife', 'Shelf Life')}</label>
                  <input type="text" name="specifications.shelfLife" value={formData.specifications.shelfLife} onChange={handleInputChange} placeholder={t('wholesaler:productForm.shelfLifePlaceholder', 'e.g., 6 months')} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.availability', 'Availability')}</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input type="checkbox" name="availability.inStock" checked={formData.availability.inStock} onChange={handleInputChange} className="mr-2 h-4 w-4" />
                  <label className="text-gray-700 font-medium">{t('wholesaler:productForm.inStock', 'In Stock')}</label>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.stockQuantity', 'Stock Quantity')}</label>
                  <input type="number" name="availability.stockQuantity" value={formData.availability.stockQuantity} onChange={handleInputChange} min="0" placeholder={t('wholesaler:productForm.stockPlaceholder', 'Optional')} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.leadTime', 'Lead Time (days)')}</label>
                  <input type="number" name="availability.leadTime" value={formData.availability.leadTime} onChange={handleInputChange} min="0" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.certifications', 'Certifications')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {certificationOptions.map(cert => (
                  <div key={cert} className="flex items-center">
                    <input type="checkbox" checked={formData.certifications.includes(cert)} onChange={() => handleCertificationChange(cert)} className="mr-2 h-4 w-4" />
                    <label className="text-gray-700">{cert}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Images */}
            <div className="pb-6">
              <h2 className="text-xl font-semibold mb-4">{t('wholesaler:productForm.images', 'Product Images')}</h2>
              {existingImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-gray-600 mb-2">{t('wholesaler:productForm.currentImages', 'Current Images:')}</p>
                  <div className="flex flex-wrap gap-3">
                    {existingImages.map((img, index) => (
                      <div key={index} className="relative">
                        <img src={getImageUrl(img)} alt={`Product ${index + 1}`} className="w-24 h-24 object-cover rounded"
                          onError={(e) => { if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled = 'true'; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='14' fill='%239ca3af'%3EError%3C/text%3E%3C/svg%3E"; }}} />
                        <button type="button" onClick={() => removeExistingImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('wholesaler:productForm.uploadImages', 'Upload Images (Max 5)')}</label>
                <input type="file" multiple accept="image/*" onChange={handleImageChange} className="w-full p-3 border border-gray-300 rounded-lg" />
              </div>
              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <p className="text-gray-600 mb-2">{t('wholesaler:productForm.newImages', 'New Images:')}</p>
                  <div className="flex flex-wrap gap-3">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img src={preview} alt={`Preview ${index + 1}`} className="w-24 h-24 object-cover rounded" />
                        <button type="button" onClick={() => removeNewImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              {!isEditMode && (
                <button type="button" onClick={(e) => handleSubmit(e, 'save-and-add-another')} disabled={loading}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 disabled:bg-gray-300 font-semibold transition-colors">
                  {loading ? t('wholesaler:productForm.saving', 'Saving...') : t('wholesaler:productForm.saveAndAdd', '✅ Save & Add Another')}
                </button>
              )}
              <button type="submit" onClick={(e) => handleSubmit(e, 'save-and-close')} disabled={loading}
                className={`${isEditMode ? 'w-full' : 'flex-1'} bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-semibold transition-colors`}>
                {loading ? t('wholesaler:productForm.saving', 'Saving...') : isEditMode ? t('wholesaler:productForm.update', 'Update Product') : t('wholesaler:productForm.saveAndClose', '💾 Save & Close')}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default ProductForm;